"use client";

import { useEffect, useState } from "react";
import { invokeProtectedFunction } from "@/lib/invokeProtectedFunction";
import type { LocalizedPricing } from "@/lib/billing/money";

/**
 * Auto-fetches token-pack + subscription prices in the buyer's local
 * (presentment) currency, detected server-side from Vercel geo-IP. No manual
 * selection — fully automatic.
 *
 * Progressive enhancement + safe default: returns `pricing: null` until the call
 * resolves, and stays null if it fails or the buyer's country can't be priced
 * (e.g. Storefront scope/permission issue) — in which case the UI shows USD and
 * checkout is created in USD too. When pricing IS present, `pricing.countryCode`
 * is forwarded into checkout so the charged currency matches what was shown.
 */
export function useLocalizedPricing() {
  const [pricing, setPricing] = useState<LocalizedPricing | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    invokeProtectedFunction<{ ok?: boolean; pricing?: LocalizedPricing | null }>(
      "get-localized-pricing",
      { body: {} },
    )
      .then(({ data, error }) => {
        if (cancelled) return;
        setPricing(!error && data?.ok ? data.pricing ?? null : null);
      })
      .catch(() => {
        if (!cancelled) setPricing(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { pricing, loading };
}
